/*
  IBL MOTION — Scroll-Reveals für die LED-Landingpage.

  Das Skript markiert Elemente selbst, statt in elf Sections
  data-Attribute zu verteilen: die Klassennamen sind unsere eigenen
  und stabil, und die Sections bleiben unangetastet.

  Sicherheitsnetz: Verstecken passiert nur hier. Läuft der Observer
  nicht (kein JS, reduzierte Bewegung, alter Browser), bleibt alles
  sichtbar. Zusätzlich deckt ein Timeout alles auf, was nach ein paar
  Sekunden immer noch versteckt ist.
*/
(function () {
  'use strict';

  var root = document.documentElement;

  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var DEFAULTS = {
    stagger: 90, // ms zwischen Geschwistern
    maxStagger: 5, // ab hier nicht weiter verzögern
    distance: 28, // px, aus denen aufgestiegen wird
    duration: 750, // ms
    threshold: 0.12,
    progress: true
  };

  // Elemente, die als Gruppe nacheinander erscheinen.
  // Lange Listen bekommen einen kürzeren Versatz und ein höheres
  // Limit — sonst landen alle ab dem sechsten Element auf demselben
  // Wert und erscheinen als Klumpen.
  var GROUPS = [
    { sel: '.ibl-lp__card' },
    { sel: '.ibl-lm__card' },
    { sel: '.ibl-lv__col' },
    { sel: '.ibl-la__step' },
    { sel: '.ibl-ls__card' },
    { sel: '.ibl-lw__item', ratio: 0.7, maxStagger: 7 },
    { sel: '.ibl-lg__tile', variant: 'media' },
    { sel: '.ibl-ic__frame', variant: 'media' },
    { sel: '.ibl-lg__tag', ratio: 0.45, maxStagger: 12 },
    { sel: '.ibl-lm__trust li', ratio: 0.5, maxStagger: 8 }
  ];

  // Einzelne Elemente
  var SOLO = [
    {
      variant: 'head',
      sel:
        '.ibl-lp__heading, .ibl-lm__heading, .ibl-lv__heading, .ibl-ic__heading, ' +
        '.ibl-li__heading, .ibl-la__heading, .ibl-ls__heading, .ibl-lg__heading, ' +
        '.ibl-lw__heading'
    },
    {
      variant: 'eyebrow',
      sel:
        '.ibl-lp__eyebrow, .ibl-lm__eyebrow, .ibl-lv__eyebrow, .ibl-ic__eyebrow, ' +
        '.ibl-li__eyebrow, .ibl-la__eyebrow, .ibl-ls__eyebrow, .ibl-lg__eyebrow, ' +
        '.ibl-lw__eyebrow'
    },
    { variant: 'media', sel: '.ibl-li__media' },
    {
      sel:
        '.ibl-lp__text, .ibl-lm__subtext, .ibl-lv__subtext, .ibl-ic__text, ' +
        '.ibl-li__text, .ibl-ls__text, .ibl-lg__subtext'
    },
    {
      sel:
        '.ibl-lv__hint, .ibl-ic__highlight, .ibl-ic__hint, ' +
        '.ibl-la__cta-wrap, .ibl-ls__cta-wrap, .ibl-li__cta'
    },
    { variant: 'num', sel: '.ibl-lp__num, .ibl-la__num', delay: 140 }
  ];

  var config = Object.assign({}, DEFAULTS);
  var observer = null;
  var pending = [];

  function readConfig() {
    var mount = document.querySelector('[data-ibl-motion-config]');
    if (!mount) return;
    ['stagger', 'distance', 'duration'].forEach(function (key) {
      var raw = mount.getAttribute('data-' + key);
      if (raw === null) return;
      var value = parseInt(raw, 10);
      if (!isNaN(value)) config[key] = value;
    });
    config.progress = mount.getAttribute('data-progress') !== 'false';
  }

  function reveal(el) {
    el.classList.add('is-in');
    var i = pending.indexOf(el);
    if (i !== -1) pending.splice(i, 1);
  }

  function mark(el, variant, delay) {
    if (!el || el.hasAttribute('data-ibl-reveal')) return;

    // Das Hero-Karussell blendet seine Slides selbst ein — ein zweiter
    // Opacity-Zustand darüber würde sich mit dem Wechsel beißen.
    if (el.closest('.ibl-lh__stage')) return;

    // Nicht verschachteln: sonst hängt ein Kind an einem Elternteil,
    // das vielleicht nie in den Viewport kommt.
    if (el.parentElement && el.parentElement.closest('[data-ibl-reveal]')) return;

    el.setAttribute('data-ibl-reveal', variant || '');
    if (delay) el.style.setProperty('--ibl-delay', delay + 'ms');
    if (config.distance !== DEFAULTS.distance) {
      el.style.setProperty('--ibl-y', config.distance + 'px');
    }
    if (config.duration !== DEFAULTS.duration) {
      el.style.setProperty('--ibl-dur', config.duration + 'ms');
    }
    pending.push(el);
    observer.observe(el);
  }

  function markGroups(scope) {
    GROUPS.forEach(function (group) {
      var buckets = new Map();
      Array.prototype.forEach.call(scope.querySelectorAll(group.sel), function (el) {
        var parent = el.parentElement || scope;
        if (!buckets.has(parent)) buckets.set(parent, []);
        buckets.get(parent).push(el);
      });
      var step = Math.round(config.stagger * (group.ratio || 1));
      var cap = group.maxStagger || config.maxStagger;
      buckets.forEach(function (items) {
        items.forEach(function (el, i) {
          mark(el, group.variant, Math.min(i, cap) * step);
        });
      });
    });
  }

  function markSolo(scope) {
    SOLO.forEach(function (entry) {
      Array.prototype.forEach.call(scope.querySelectorAll(entry.sel), function (el) {
        mark(el, entry.variant, entry.delay || 0);
      });
    });
  }

  function scan(scope) {
    markGroups(scope || document);
    markSolo(scope || document);
  }

  function buildProgressBar() {
    if (!config.progress) return;

    var wrap = document.createElement('div');
    wrap.className = 'ibl-progress';
    wrap.setAttribute('aria-hidden', 'true');
    var bar = document.createElement('span');
    bar.className = 'ibl-progress__bar';
    wrap.appendChild(bar);
    document.body.appendChild(wrap);

    var ticking = false;
    function paint() {
      var max = document.documentElement.scrollHeight - window.innerHeight;
      var ratio = max > 0 ? window.scrollY / max : 0;
      bar.style.transform = 'scaleX(' + Math.min(1, Math.max(0, ratio)) + ')';
      ticking = false;
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(paint);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    paint();
  }

  function init() {
    readConfig();
    root.classList.add('ibl-motion');

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: config.threshold, rootMargin: '0px 0px -8% 0px' }
    );

    scan(document);
    buildProgressBar();

    // Falls irgendetwas nie beobachtet wird, darf es nicht versteckt bleiben.
    window.setTimeout(function () {
      pending.slice().forEach(reveal);
    }, 5000);

    // Theme-Editor: neu geladene Sections nachtragen.
    document.addEventListener('shopify:section:load', function (e) {
      if (e.target && e.target.querySelectorAll) scan(e.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
